import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Typography,
} from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { showModal } from "../../app/store/slice/counterSlice";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import { routesDropDown } from "../../app/utlils";
import { FormAutoComplete } from "./FormAutoComplete";
import { useDispatch, useSelector } from "react-redux";
import { FormInputDropdown2 } from "./FormInputDropdown2";
import { FormInputDropdown } from "./FormInputDropdown";
import { motion } from "motion/react";
import { FormInputDateTime } from "./FormInputDateTime";
import { FormInputText } from "./FormInputText";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

// ✅ Module-level — survives page navigation (remounts), cleared only on full page refresh
// Tracks which banners were manually deactivated so auto-scheduler skips them
const manuallyDeactivatedBanners = new Set();

// ✅ Returns API endpoints and field names based on entity ("banner" | "offer")
const getEntityConfig = (entity = "banner") => {
  const isBanner = entity === "banner";
  return {
    uploadApi: isBanner ? "/imageUpdateBanner" : "/imageUpdateOfferBanner",
    switchApi: isBanner ? "/bannerSwitch/" : "/offerSwitch/",
    imageField: isBanner ? "banner_image" : "offer_banner_image",
    idField: isBanner ? "banner_id" : "offer_id",
    fromDateField: isBanner ? "banner_from_date" : "offer_from_date",
    toDateField: isBanner ? "banner_to_date" : "offer_to_date",
  };
};

// ✅ Separate component so useWatch hook is valid (no hooks inside conditionals)
const PromoSection = ({
  control,
  errors,
  promoList,
  customPromoList,
  routeValue,
}) => {
  const selectedPromoId = useWatch({ control, name: "promo_id" });
  const selectedCustomPromoId = useWatch({ control, name: "custom_promo_id" });

  // ✅ Filter active only
  const activePromos = promoList.filter((p) => p.active_status === 1);
  const activeCustomPromos = customPromoList.filter(
    (p) => p.active_status === 1,
  );

  const selectedPromo = activePromos.find(
    (p) => String(p.id) === String(selectedPromoId ?? routeValue?.promo_id),
  );
  const selectedCustomPromo = activeCustomPromos.find(
    (p) =>
      String(p.cust_promo_id) ===
      String(selectedCustomPromoId ?? routeValue?.custom_promo_id),
  );

  return (
    <Grid container columnSpacing={{ xs: 4 }}>
      {/* ✅ Existing Promo dropdown - active only */}
      <Grid item xs={12}>
        <FormInputDropdown
          name="promo_id"
          control={control}
          label="Promo Code"
          type="single"
          options={[
            { id: "", name: "--None--" },
            ...activePromos.map((p) => ({
              id: p.id,
              name: `${p.promo_code}${p.discount ? ` (${p.type === "PERCENTAGE" ? p.discount + "%" : "₹" + p.discount})` : ""}`,
            })),
          ]}
          errors={errors}
          defaultValue={routeValue?.promo_id || ""}
        />
      </Grid>

      {selectedPromo && (
        <Grid item xs={12}>
          <div
            style={{
              backgroundColor: "#f0f9ff",
              border: "1px solid #bae6fd",
              borderRadius: 8,
              padding: "10px 14px",
              marginTop: 8,
              fontSize: 13,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 16,
                flexWrap: "wrap",
                marginBottom: 6,
              }}
            >
              <span>
                <strong>Min Value:</strong> ₹{selectedPromo.min_value}
              </span>
              <span>
                <strong>Discount:</strong>{" "}
                {selectedPromo.type === "PERCENTAGE"
                  ? `${selectedPromo.discount}%`
                  : `₹${selectedPromo.discount}`}
              </span>
              <span>
                <strong>Type:</strong>{" "}
                {selectedPromo.type === "PERCENTAGE" ? "Percentage" : "Amount"}
              </span>
            </div>
            {selectedPromo.terms_conditions && (
              <div
                style={{
                  color: "#475569",
                  whiteSpace: "pre-line",
                  lineHeight: 1.5,
                }}
              >
                <strong>Terms & Conditions:</strong>
                <br />
                {selectedPromo.terms_conditions}
              </div>
            )}
          </div>
        </Grid>
      )}

      {/* ✅ NEW: Custom Promo dropdown - active only */}
      <Grid item xs={12} sx={{ mt: 1 }}>
        <FormInputDropdown
          name="custom_promo_id"
          control={control}
          label="Custom Promo Code"
          type="single"
          options={[
            { id: "", name: "--None--" },
            ...activeCustomPromos.map((p) => ({
              id: p.cust_promo_id,
              name: `${p.promo_code}${p.discount ? ` (${p.type === "PERCENTAGE" ? p.discount + "%" : "₹" + p.discount})` : ""}`,
            })),
          ]}
          errors={errors}
          defaultValue={routeValue?.custom_promo_id || ""}
        />
      </Grid>

      {selectedCustomPromo && (
        <Grid item xs={12}>
          <div
            style={{
              backgroundColor: "#fff7ed",
              border: "1px solid #fed7aa",
              borderRadius: 8,
              padding: "10px 14px",
              marginTop: 8,
              fontSize: 13,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 16,
                flexWrap: "wrap",
                marginBottom: 6,
              }}
            >
              <span>
                <strong>Min Amount:</strong> ₹{selectedCustomPromo.min_amount}
              </span>
              <span>
                <strong>Discount:</strong>{" "}
                {selectedCustomPromo.type === "PERCENTAGE"
                  ? `${selectedCustomPromo.discount}%`
                  : `₹${selectedCustomPromo.discount}`}
              </span>
              <span>
                <strong>New User Only:</strong>{" "}
                {selectedCustomPromo.new_user === 1 ? "Yes" : "No"}
              </span>
            </div>
            {selectedCustomPromo.terms_conditions && (
              <div
                style={{
                  color: "#475569",
                  whiteSpace: "pre-line",
                  lineHeight: 1.5,
                }}
              >
                <strong>Terms & Conditions:</strong>
                <br />
                {selectedCustomPromo.terms_conditions}
              </div>
            )}
          </div>
        </Grid>
      )}

      <Grid item xs={12}>
        <Divider
          sx={{ mt: 1.5, mb: 1, border: "1.5px solid", borderColor: "primary" }}
        />
      </Grid>
    </Grid>
  );
};

const RouteDialog = React.memo(
  ({
    open,
    onClose,
    onSave,
    control,
    errors,
    setValue,
    routeValue,
    routeList,
    entity,
    offerName,
  }) => {
    const config = getEntityConfig(entity);
    const isOffer = entity === "offer";

    // ✅ Pull promo list from Redux store for offer dropdown
    const promoList = useSelector((state) => state.counter?.promoList || []);
    const customPromoList = useSelector(
      (state) => state.counter?.customPromoList || [],
    );

    // Only used for banner entity route handling
    const routeType =
      useWatch({ control, name: "route_type" }) || routeValue?.route_type;
    const SELECT_OPTION = { value: "", label: "--SELECT PRODUCT--" };

    useEffect(() => {
      if (!isOffer) setValue("route_item", "");
    }, [routeType]);

    useEffect(() => {
      if (open && routeValue) {
        if (!isOffer) {
          setValue("route_type", routeValue.route_type || "");
          setValue("route_item", routeValue.route_item || "");
        }
        // promo_id for offer
        if (isOffer) {
          setValue("promo_id", routeValue.promo_id || "");
          setValue("offer_name", offerName || "");
        }

        setTimeout(() => {
          if (routeValue[config.fromDateField]) {
            setValue(
              config.fromDateField,
              dayjs(routeValue[config.fromDateField]),
            );
          }
          if (routeValue[config.toDateField]) {
            setValue(config.toDateField, dayjs(routeValue[config.toDateField]));
          }
        }, 100);
      }
    }, [open, routeValue, setValue, offerName]);

    return (
      <Dialog fullWidth={true} open={open} onClose={onClose} maxWidth={"sm"}>
        <DialogTitle className="text-primary">Advanced Settings</DialogTitle>
        <DialogContent>
          {/* ✅ Route section — only shown for banner entity */}
          {!isOffer && (
            <>
              <Typography variant="h6" className="mb-1">
                Route
              </Typography>
              <Grid container columnSpacing={{ xs: 4 }}>
                <Grid item xs={12}>
                  <Controller
                    defaultValue={routeValue?.route_type}
                    render={({ field: { onChange, value } }) => {
                      return (
                        <FormControl
                          fullWidth
                          error={errors?.["route_type"]?.message ? true : false}
                          margin="normal"
                        >
                          <InputLabel id="route_type_label">
                            {" "}
                            Category
                          </InputLabel>
                          <Select
                            labelId="route_type_label"
                            id={`route_type_elect`}
                            value={value || ""}
                            label="Category"
                            onChange={(e) => {
                              onChange(e);
                              setValue("route_item", "");
                            }}
                          >
                            <MenuItem value={""} key={0}>
                              --SELECT--
                            </MenuItem>
                            {routesDropDown?.map((item) => (
                              <MenuItem value={item?.id} key={item?.id}>
                                {item?.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    }}
                    control={control}
                    name={"route_type"}
                  />
                </Grid>
                <Grid item xs={12}>
                  {routeType == "C" && (
                    <FormInputDropdown
                      name="route_item"
                      control={control}
                      label="Category"
                      type="single"
                      options={routeList?.category?.map((item) => ({
                        id: item?.id,
                        name: item?.category_name,
                      }))}
                      errors={errors}
                      defaultValue={routeValue?.route_item || ""}
                      mandatory="true"
                    />
                  )}
                  {routeType == "SC" && (
                    <FormInputDropdown
                      name="route_item"
                      control={control}
                      label="Sub Category"
                      type="single"
                      options={routeList?.sub_category?.map((item) => ({
                        id: item?.id,
                        name: item?.sub_category_name,
                      }))}
                      errors={errors}
                      defaultValue={routeValue?.route_item || ""}
                      mandatory="true"
                    />
                  )}
                  {routeType == "P" && (
                    <FormAutoComplete
                      name="route_item"
                      control={control}
                      label="Product"
                      multiple={false}
                      dataArray={[
                        SELECT_OPTION,
                        ...routeList?.product?.map((item) => ({
                          value: item?.id,
                          label: item?.product_name,
                        })),
                      ]}
                      errors={errors}
                      defaultValue={{
                        value: routeValue?.route_item || SELECT_OPTION?.value,
                        label:
                          routeList?.product?.find(
                            (item) => item?.id == routeValue?.route_item,
                          )?.product_name || SELECT_OPTION?.label,
                      }}
                      mandatory="true"
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Divider
                    sx={{
                      mt: 3,
                      border: "1.5px solid",
                      borderColor: "primary",
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}
          {/* ✅ Offer Name field — only shown for offer entity */}
          {isOffer && (
            <Grid container columnSpacing={{ xs: 4 }}>
              <Grid item xs={12}>
                <Typography variant="h6" className="mb-1">
                  Offer Name
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormInputText
                  name="offer_name"
                  control={control}
                  label="Offer Banner Name"
                  errors={errors}
                  mandatory="true"
                  defaultValue={offerName || ""}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider
                  sx={{
                    mt: 1.5,
                    mb: 1,
                    border: "1.5px solid",
                    borderColor: "primary",
                  }}
                />
              </Grid>
            </Grid>
          )}
          {/* ✅ Promo section — only shown for offer entity */}
          {isOffer && (
            <PromoSection
              control={control}
              errors={errors}
              promoList={promoList}
              customPromoList={customPromoList}
              routeValue={routeValue}
            />
          )}

          {/* ✅ Schedule section — always shown */}
          <Grid container columnSpacing={{ xs: 4 }}>
            <Grid item xs={12}>
              <Typography variant="h6" className="my-3">
                Schedule {entity === "offer" ? "Offer" : "Banner"}
              </Typography>
            </Grid>
            <Grid item xs={6} style={{ width: "100%" }}>
              <FormInputDateTime
                name={config.fromDateField}
                control={control}
                label="From Date"
                defaultValue={
                  routeValue?.[config.fromDateField]
                    ? dayjs(routeValue[config.fromDateField])
                    : null
                }
                errors={errors}
                mandatory="true"
              />
            </Grid>
            <Grid item xs={6} style={{ width: "100%" }}>
              <FormInputDateTime
                name={config.toDateField}
                control={control}
                label="To Date"
                defaultValue={
                  routeValue?.[config.toDateField]
                    ? dayjs(routeValue[config.toDateField])
                    : null
                }
                errors={errors}
                mandatory="true"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained" color="primary">
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

const ImageUpload = ({
  name,
  label,
  status,
  defaultValue,
  mandatory,
  loadInitialData,
  regionId,
  routeValue,
  routeList,
  platform,
  entity = "banner", // ✅ "banner" (default) | "offer"
}) => {
  const config = getEntityConfig(entity);
  const isOffer = entity === "offer";

  const dispatch = useDispatch();
  const methods = useForm();
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const aRef = useRef(null);
  const selectedImageRef = useRef({});
  const [imageUrl, setImageUrl] = useState("");
  const [selectedImage, setSelectedImage] = useState({});
  const [loader, setLoader] = useState(false);
  const [open, setOpen] = useState(false);
  const [routeOpen, setRouteOpen] = useState(false);
  const [now, setNow] = useState(dayjs());
  const [currentStatus, setCurrentStatus] = useState(status);

  const handleRoute = () => {
    setRouteOpen(true);
  };

  const clearCache = async () => {
    try {
      await axios.get("clear-cache");
    } catch (err) {}
  };

  useEffect(() => {
    if (defaultValue) {
      setImageUrl(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    setCurrentStatus(status);
  }, [status]);

  // const autoToggleBanner = useCallback(async () => {
  //   try {
  //     const response = await axios.get(config.switchApi + name);
  //     if (!response?.data?.error) {
  //       setCurrentStatus((prev) => (prev === 1 ? 0 : 1));
  //       await clearCache();
  //       loadInitialData();
  //     }
  //   } catch (error) {}
  // }, [name, loadInitialData, config.switchApi]);
  // ✅ Fix — add this ref just before autoToggleBanner
  const loadInitialDataRef = useRef(loadInitialData);
  useEffect(() => {
    loadInitialDataRef.current = loadInitialData;
  }, [loadInitialData]);

  const autoToggleBanner = useCallback(async () => {
    try {
      const response = await axios.get(config.switchApi + name);
      if (!response?.data?.error) {
        setCurrentStatus((prev) => (prev === 1 ? 0 : 1));
        await clearCache();
        loadInitialDataRef.current(); // ✅ always latest, no dep needed
      }
    } catch (error) {}
  }, [name, config.switchApi]); // ✅ loadInitialData removed from deps
  useEffect(() => {
    if (
      !routeValue?.[config.fromDateField] ||
      !routeValue?.[config.toDateField]
    )
      return;

    if (manuallyDeactivatedBanners.has(name)) return;

    const start = dayjs(routeValue[config.fromDateField]);
    const end = dayjs(routeValue[config.toDateField]);
    const now = dayjs();

    let startTimeout, endTimeout;

    const MAX_TIMEOUT = 2_147_483_647;

    const safeSchedule = (fn, delay, targetDate) => {
      if (delay <= MAX_TIMEOUT) {
        return { type: "timeout", id: setTimeout(fn, delay) };
      } else {
        const id = setInterval(() => {
          if (dayjs().isAfter(dayjs(targetDate))) {
            clearInterval(id);
            fn();
          }
        }, 30_000);
        return { type: "interval", id };
      }
    };

    const clearSafe = (handle) => {
      if (!handle) return;
      handle.type === "timeout"
        ? clearTimeout(handle.id)
        : clearInterval(handle.id);
    };

    if (now.isBefore(start) && currentStatus === 0) {
      startTimeout = safeSchedule(
        autoToggleBanner,
        start.diff(now),
        routeValue[config.fromDateField],
      );
    }

    if (now.isBefore(end) && currentStatus === 1) {
      endTimeout = safeSchedule(
        autoToggleBanner,
        end.diff(now),
        routeValue[config.toDateField],
      );
    }

    return () => {
      clearSafe(startTimeout);
      clearSafe(endTimeout);
    };
  }, [
    routeValue?.[config.fromDateField],
    routeValue?.[config.toDateField],
    autoToggleBanner,
    name,
    currentStatus,
  ]);

  const [imageValid, setImageValid] = useState(true);
  // const fileHandler = (e) => {
  //   const file = e.target.files[0];
  //   if (!file) return;

  //   if (platform === "web") {
  //     setImageValid(false);
  //     const objectUrl = URL.createObjectURL(file);
  //     const img = new Image();
  //     img.onload = () => {
  //       URL.revokeObjectURL(objectUrl);
  //       if (img.width !== 1500 || img.height !== 500) {
  //         toast.error(
  //           ` upload web image . Your image is ${img.width}×${img.height}px.`,
  //         );
  //         e.target.value = "";
  //         setSelectedImage({});
  //         setImageUrl("");
  //         setImageValid(true);
  //         return;
  //       }
  //       setSelectedImage({ name: file });
  //       selectedImageRef.current = { name: file };
  //       setImageUrl(URL.createObjectURL(file));
  //       setImageValid(true);
  //     };
  //     img.src = objectUrl;
  //   } else {
  //     setSelectedImage({ name: file });
  //     selectedImageRef.current = { name: file };
  //     setImageUrl(URL.createObjectURL(file));
  //     setImageValid(true);
  //   }
  // };
  const fileHandler = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (platform === "web") {
      setImageValid(false);
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);

        // ✅ Offer web image dimension check — banner skips this entirely
        if (isOffer) {
          const requiredWidth = 1160;
          const requiredHeight = 870;
          if (img.width !== requiredWidth || img.height !== requiredHeight) {
            toast.error(
              `Invalid dimensions. Expected ${requiredWidth}×${requiredHeight}px. Your image is ${img.width}×${img.height}px.`,
            );
            e.target.value = "";
            setSelectedImage({});
            setImageUrl("");
            setImageValid(true);
            return;
          }
        }

        // ✅ Banner — existing 1500×500 check unchanged
        if (!isOffer && (img.width !== 1500 || img.height !== 500)) {
          toast.error(
            ` upload web image . Your image is ${img.width}×${img.height}px.`,
          );
          e.target.value = "";
          setSelectedImage({});
          setImageUrl("");
          setImageValid(true);
          return;
        }

        setSelectedImage({ name: file });
        selectedImageRef.current = { name: file };
        setImageUrl(URL.createObjectURL(file));
        setImageValid(true);
      };
      img.src = objectUrl;
    } else {
      // App — no validation for either entity
      setSelectedImage({ name: file });
      selectedImageRef.current = { name: file };
      setImageUrl(URL.createObjectURL(file));
      setImageValid(true);
    }
  };
  const onSubmit = (data) => upload(data);

  const upload = (data) => {
    const currentImage = selectedImageRef.current;
    console.log("currentImage:", currentImage);
    console.log("data:", data);

    // ✅ For offer: trigger on image OR promo_id OR dates
    // ✅ For banner: trigger on image OR route_type OR dates
    const hasChanges = isOffer
      ? currentImage?.name ||
        data?.promo_id !== undefined ||
        data?.custom_promo_id !== undefined || // 👈 add this
        data?.offer_name !== undefined ||
        data?.[config.fromDateField]
      : currentImage?.name || data?.route_type;

    if (hasChanges) {
      setLoader(true);
      const formData = new FormData();
      if (currentImage?.name)
        formData.append(config.imageField, currentImage?.name);
      formData.append(config.idField, name);
      formData.append("region_id", regionId);
      formData.append("platform", platform);

      if (isOffer) {
        formData.append("promo_id", data?.promo_id ?? ""); // 👈 regular promo
        formData.append("custom_promo_id", data?.custom_promo_id ?? ""); // 👈 custom promo
        formData.append("offer_name", data?.offer_name ?? "");
      } else {
        // ✅ Banner entity: send route fields
        formData.append("route_type", data?.route_type);
        if (data?.route_type === "P") {
          formData.append("route_item", data?.route_item?.value ?? "");
        } else {
          formData.append("route_item", data?.route_item ?? "");
        }
      }

      // ✅ Schedule dates — always sent for both entities
      if (data?.[config.fromDateField] && data?.[config.toDateField]) {
        formData.append(
          config.fromDateField,
          dayjs(data[config.fromDateField]).format("YYYY-MM-DD HH:mm:ss"),
        );
        formData.append(
          config.toDateField,
          dayjs(data[config.toDateField]).format("YYYY-MM-DD HH:mm:ss"),
        );
      }

      axios
        .post(config.uploadApi, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((response) => {
          if (!response?.data?.error) {
            if (
              typeof response?.data?.data === "string" &&
              response?.data?.data?.startsWith("http")
            ) {
              setImageUrl(response?.data?.data);
            }
            loadInitialData();
            dispatch(
              showModal({
                msg: response?.data?.message,
                error: response?.data?.error,
              }),
            );
          } else {
            let errDesc = "";
            Object.values(response?.data?.data).forEach((error, index) => {
              errDesc = index === 0 ? errDesc + error : errDesc + error + "\n ";
            });
            dispatch(
              showModal({
                msg: errDesc || response?.data?.message,
                error: response?.data?.error,
              }),
            );
          }
          setLoader(false);
        })
        .catch((error) => {
          let msg = error;
          if (error?.code === "ERR_BAD_REQUEST") {
            msg = error?.response?.data?.message;
          } else {
            msg = error?.message;
          }
          dispatch(
            showModal({
              msg: msg,
              error: true,
            }),
          );
        })
        .finally(() => {
          setLoader(false);
          setOpen(false);
        });
    }
  };

  const toggleBannerStatusHandler = async () => {
    setLoader(true);
    try {
      const response = await axios.get(config.switchApi + name);
      if (!response?.data?.error) {
        const isDeactivating = currentStatus === 1;
        setCurrentStatus((prev) => (prev === 1 ? 0 : 1));
        await clearCache();

        if (
          isDeactivating &&
          routeValue?.[config.fromDateField] &&
          routeValue?.[config.toDateField]
        ) {
          manuallyDeactivatedBanners.add(name);

          const nowFormatted = dayjs().format("YYYY-MM-DD HH:mm:ss");
          const formData = new FormData();
          formData.append(config.idField, name);
          formData.append("region_id", regionId);
          formData.append("platform", platform);
          formData.append(
            config.fromDateField,
            routeValue[config.fromDateField],
          );
          formData.append(config.toDateField, nowFormatted);
          await axios.post(config.uploadApi, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          loadInitialData();
        } else if (!isDeactivating) {
          manuallyDeactivatedBanners.delete(name);
        }
      } else {
        let errDesc = "";
        Object.values(response?.data?.data).map((error, index) => {
          errDesc = index === 0 ? errDesc + error : errDesc + error + "\n ";
        });
      }
    } catch (error) {
      toast.error(error?.message);
    } finally {
      setLoader(false);
      setOpen(false);
    }
  };

  const getRouteDisplayText = () => {
    if (!routeValue?.route_type) return null;

    let routeText = "";
    const routeTypeName =
      routesDropDown?.find((item) => item?.id === routeValue?.route_type)
        ?.name || "";

    if (routeValue?.route_type === "C") {
      const categoryName =
        routeList?.category?.find((item) => item?.id === routeValue?.route_item)
          ?.category_name || "";
      routeText = `${routeTypeName}: ${categoryName}`;
    } else if (routeValue?.route_type === "SC") {
      const subCategoryName =
        routeList?.sub_category?.find(
          (item) => item?.id === routeValue?.route_item,
        )?.sub_category_name || "";
      routeText = `${routeTypeName}: ${subCategoryName}`;
    } else if (routeValue?.route_type === "P") {
      const productName =
        routeList?.product?.find((item) => item?.id === routeValue?.route_item)
          ?.product_name || "";
      routeText = `${routeTypeName}: ${productName}`;
    }

    return routeText;
  };

  const isWithinSchedule = () => {
    if (
      !routeValue?.[config.fromDateField] ||
      !routeValue?.[config.toDateField]
    )
      return false;
    const start = dayjs(routeValue[config.fromDateField]);
    const end = dayjs(routeValue[config.toDateField]);
    return now.isAfter(start) && now.isBefore(end);
  };

  return (
    <>
      <Paper
        elevation={1}
        sx={{
          p: 3,
          margin: "auto",
          marginTop: "15px",
          flexGrow: 1,
        }}
      >
        <div>
          <label
            htmlFor="formFile"
            className="form-label"
            style={{ textTransform: "capitalize" }}
          >
            {label}
            {mandatory && (
              <span style={{ color: "red", paddingLeft: "2px" }}>*</span>
            )}
          </label>

          <div className="row">
            <div>
              <input
                ref={aRef}
                className="form-control"
                name={name}
                type="file"
                onChange={fileHandler}
                accept="image/png, image/jpeg, image/webp"
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12 banner-card mt-4 p-0">
              {imageUrl ? (
                <PhotoProvider>
                  <PhotoView key={name} src={imageUrl}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      style={{ position: "relative" }}
                    >
                      <img
                        src={imageUrl}
                        alt=""
                        className="w-100 h-100"
                        style={{
                          cursor: "pointer",
                        }}
                      />

                      {(routeValue?.[config.fromDateField] ||
                        routeValue?.[config.toDateField]) && (
                        <div
                          style={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            backgroundColor: "rgba(0,0,0,0.6)",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: "bold",
                            userSelect: "none",
                            pointerEvents: "none",
                            whiteSpace: "nowrap",
                          }}
                          title={
                            routeValue[config.fromDateField] &&
                            routeValue[config.toDateField]
                              ? `Activate on ${dayjs(
                                  routeValue[config.fromDateField],
                                ).format(
                                  "YYYY-MM-DD HH:mm",
                                )} | Deactivate on ${dayjs(
                                  routeValue[config.toDateField],
                                ).format("YYYY-MM-DD HH:mm")}`
                              : ""
                          }
                        >
                          {routeValue[config.fromDateField] &&
                          now.isBefore(
                            dayjs(routeValue[config.fromDateField]),
                          ) ? (
                            <>
                              Activates in{" "}
                              {dayjs(routeValue[config.fromDateField]).fromNow(
                                true,
                              )}
                            </>
                          ) : routeValue[config.fromDateField] &&
                            now.isAfter(
                              dayjs(routeValue[config.fromDateField]),
                            ) ? (
                            <>
                              {routeValue[config.toDateField] &&
                              now.isBefore(
                                dayjs(routeValue[config.toDateField]),
                              ) ? (
                                <>
                                  Deactivates in{" "}
                                  {dayjs(
                                    routeValue[config.toDateField],
                                  ).fromNow(true)}
                                </>
                              ) : (
                                <>
                                  Deactivated on{" "}
                                  {dayjs(routeValue[config.toDateField]).format(
                                    "MMM D, HH:mm",
                                  )}
                                </>
                              )}
                            </>
                          ) : null}
                        </div>
                      )}
                    </motion.div>
                  </PhotoView>
                </PhotoProvider>
              ) : (
                <h1>
                  {platform == "app"
                    ? isOffer
                      ? "4:5 ratio"
                      : "2:1 ratio"
                    : isOffer
                      ? "4:3 ratio"
                      : "16:9"}
                </h1>
              )}
            </div>
          </div>
          <div className="row">
            <div className="col m-2 justify-content-center d-inline-flex">
              <Button
                onClick={() => handleRoute(name)}
                sx={{
                  fontSize: "13px",
                  textTransform: "capitalize",
                }}
              >
                Advance Option
              </Button>
            </div>
          </div>

          <div className="row">
            <div className="col gap-3 justify-content-center d-inline-flex">
              {imageUrl && !loader && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={handleSubmit(onSubmit)}
                  disabled={loader}
                >
                  Save
                </Button>
              )}

              {imageUrl && !loader && (
                <>
                  {currentStatus === 1 && isWithinSchedule() && (
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      onClick={() => setOpen(true)}
                      disabled={loader}
                    >
                      Deactivate
                    </Button>
                  )}

                  {currentStatus === 0 && isWithinSchedule() && (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => setOpen(true)}
                      disabled={loader}
                    >
                      Activate
                    </Button>
                  )}
                </>
              )}

              {loader && (
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only"></span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to{" "}
            {currentStatus == 1 ? "Deactivate" : "Activate"} ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            variant="contained"
            color="error"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => toggleBannerStatusHandler()}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {routeOpen && (
        <RouteDialog
          open={routeOpen}
          onClose={() => setRouteOpen(false)}
          onSave={() => {
            // ✅ ADD
            setRouteOpen(false);
            handleSubmit(onSubmit)(); // ✅ trigger upload immediately
          }}
          control={control}
          errors={errors}
          setValue={setValue}
          routeValue={routeValue}
          routeList={routeList}
          entity={entity}
          offerName={label}
        />
      )}
    </>
  );
};

export default memo(ImageUpload);
